import * as THREE from 'three'

/** Owns the three.js starfield + case-file specimen scene behind the console UI. */
export class LabScene {
  constructor(canvas) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(window.innerWidth, window.innerHeight)

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 2000)
    this.camera.position.set(0, 0, 60)
    this.scene.fog = new THREE.FogExp2(0x000308, 0.0016)

    this.clock = new THREE.Clock()
    this.mouse = new THREE.Vector2()
    this.alertMode = false
    this.warpBoost = 0

    this.stars = this.createStarfield()
    this.scene.add(this.stars)

    this.specimen = this.createSpecimen()
    this.scene.add(this.specimen)

    this.scanRing = this.createScanRing()
    this.scene.add(this.scanRing)

    this.scene.add(new THREE.AmbientLight(0x224466, 1.2))
    const key = new THREE.PointLight(0xff9900, 2, 300)
    key.position.set(30, 20, 40)
    this.scene.add(key)
    const rim = new THREE.PointLight(0x33ccff, 1.5, 300)
    rim.position.set(-30, -10, -20)
    this.scene.add(rim)

    window.addEventListener('resize', () => this.onResize())
    window.addEventListener('pointermove', (e) => this.onPointerMove(e))
  }

  createStarfield() {
    const count = 4000
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const palette = [
      new THREE.Color(0xffffff),
      new THREE.Color(0x99ccff),
      new THREE.Color(0xffcc99),
      new THREE.Color(0x33ffcc),
    ]

    for (let i = 0; i < count; i++) {
      const radius = 200 + Math.random() * 700
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = radius * Math.cos(phi)

      const c = palette[Math.floor(Math.random() * palette.length)]
      colors[i * 3] = c.r
      colors[i * 3 + 1] = c.g
      colors[i * 3 + 2] = c.b
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

    const material = new THREE.PointsMaterial({
      size: 1.6,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      sizeAttenuation: true,
    })

    return new THREE.Points(geometry, material)
  }

  createSpecimen() {
    const group = new THREE.Group()

    const coreGeo = new THREE.IcosahedronGeometry(9, 1)
    const coreMat = new THREE.MeshStandardMaterial({
      color: 0x113344,
      emissive: 0x33ccff,
      emissiveIntensity: 0.4,
      roughness: 0.35,
      metalness: 0.6,
      transparent: true,
      opacity: 0.55,
    })
    group.add(new THREE.Mesh(coreGeo, coreMat))

    const wireGeo = new THREE.IcosahedronGeometry(9.4, 1)
    const wireMat = new THREE.MeshBasicMaterial({ color: 0x33ffcc, wireframe: true })
    group.add(new THREE.Mesh(wireGeo, wireMat))

    return group
  }

  createScanRing() {
    const geo = new THREE.TorusGeometry(14, 0.15, 8, 96)
    const mat = new THREE.MeshBasicMaterial({ color: 0xff9900, transparent: true, opacity: 0.8 })
    const ring = new THREE.Mesh(geo, mat)
    ring.rotation.x = Math.PI / 2.4
    return ring
  }

  onResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(window.innerWidth, window.innerHeight)
  }

  onPointerMove(e) {
    this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1
    this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1
  }

  setAlertMode(active) {
    this.alertMode = active
    const wireMat = this.specimen.children[1].material
    const coreMat = this.specimen.children[0].material
    const ringMat = this.scanRing.material
    wireMat.color.set(active ? 0xff3b30 : 0x33ffcc)
    coreMat.emissive.set(active ? 0xff3b30 : 0x33ccff)
    ringMat.color.set(active ? 0xff3b30 : 0xff9900)
    this.scene.fog.color.set(active ? 0x1a0000 : 0x000308)
  }

  /** Briefly accelerates the starfield to suggest scanning through case history. */
  triggerWarp() {
    this.warpBoost = 1
  }

  start() {
    const animate = () => {
      requestAnimationFrame(animate)
      const t = this.clock.getElapsedTime()

      this.specimen.rotation.y = t * 0.25
      this.specimen.rotation.x = Math.sin(t * 0.3) * 0.15
      this.scanRing.rotation.z = t * 0.4

      this.stars.rotation.y = t * 0.01
      if (this.warpBoost > 0.01) {
        this.stars.position.z += this.warpBoost * 6
        this.warpBoost *= 0.94
      } else {
        this.stars.position.z *= 0.9
      }

      const targetX = this.mouse.x * 4
      const targetY = this.mouse.y * 2
      this.camera.position.x += (targetX - this.camera.position.x) * 0.03
      this.camera.position.y += (targetY - this.camera.position.y) * 0.03
      if (this.alertMode) {
        this.camera.position.x += (Math.random() - 0.5) * 0.15
        this.camera.position.y += (Math.random() - 0.5) * 0.15
      }
      this.camera.lookAt(0, 0, 0)

      this.renderer.render(this.scene, this.camera)
    }
    animate()
  }
}
